import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  Typography,
  createStyles,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  Theme,
} from '@bb-ui/react-library';
import useRestApi from 'hooks/useRestApi';
import { regionalApiUrl } from 'utils/apiUrl';
import { useEffect } from 'react';
import { TenantHierarchyResponse, HierarchyTenant, TenantData } from 'App.types';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { getComparedRegion, useLearnDataExport } from 'hooks/useLearnDataExport';
import {
  BooleanStatusIcon,
  DataExportStatus,
  RegionMismatchWarning,
} from 'components/DataSharing/DataExportStatus';

// Shared with the linked Illuminate section, so both data sharing tables look the same
export const useDataSharingTableStyles = makeStyles((theme: Theme) => {
  const sharingBackground = `color-mix(in srgb, ${theme.palette.primary.main} 6%, transparent)`;

  return createStyles({
    heading: {
      marginBottom: '14px',
    },
    section: {
      marginBottom: '45px',
    },
    tenantLink: {
      color: theme.palette.text.secondary,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    tableCell: {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.fontSizeSmall,
    },
    tenantIdCell: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    groupHeader: {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.fontSizeSmall,
      textTransform: 'uppercase',
      borderBottom: 'none',
      paddingBottom: 0,
    },
    validationHeader: {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.fontSizeSmall,
      fontWeight: 'normal',
    },
    // Data sharing and reporting are the main information of the table,
    // so they get a separator, a light background and bold headers.
    sharingGroupHeader: {
      color: theme.palette.text.primary,
      fontWeight: 'bold',
      borderLeft: `2px solid ${theme.palette.divider}`,
      textAlign: 'center',
    },
    sharingHeader: {
      fontWeight: 'bold',
      textAlign: 'center',
      backgroundColor: sharingBackground,
    },
    sharingCell: {
      textAlign: 'center',
      backgroundColor: sharingBackground,
    },
    sharingFirstColumn: {
      borderLeft: `2px solid ${theme.palette.divider}`,
    },
  });
});

export interface DataSharingSectionProps {
  tenant: Partial<TenantData>;
}

export const DataSharingSection: React.FC<DataSharingSectionProps> = ({ tenant }) => {
  const { t } = useTranslation();
  const classes = useDataSharingTableStyles();
  const [hierarchyTenants, setHierarchyTenants] = React.useState<HierarchyTenant[]>([]);
  const { stage } = useAppConfigContext();
  const requestingRegion = tenant.region;

  const {
    data: hierarchyData,
    error: hierarchyError,
    loading: hierarchyLoading,
  } = useRestApi<TenantHierarchyResponse>(
    regionalApiUrl({
      capabilityName: 'data',
      region: tenant.region || 'us-east-1',
      apiPath: `tenants/illuminate/${tenant.id}/hierarchy`,
      stage,
    }),
  );

  const { dataExport: learnDataExport, resourceRegions } = useLearnDataExport(
    hierarchyData?.tenants,
  );

  useEffect(() => {
    if (!hierarchyData) {
      setHierarchyTenants([]);
      return;
    }

    const allTenants = hierarchyData.tenants || [];

    // Filter out the current tenant and keep only allowed tenant types (learn, ally, or null).
    // Tenants from other regions stay in the list with a warning.
    const allowedTypes = ['learn', 'ally'];
    const tenantsInHierarchy = allTenants.filter(
      (t) =>
        t.id !== tenant.id && // remove current tenant
        (t.tenantType === null || allowedTypes.includes(t.tenantType?.toLowerCase() ?? '')),
    );

    // Sort: shared tenants first
    tenantsInHierarchy.sort((a, b) => {
      if (a.dataSharingInd && !b.dataSharingInd) return -1;
      if (!a.dataSharingInd && b.dataSharingInd) return 1;
      return 0;
    });

    setHierarchyTenants(tenantsInHierarchy);
  }, [hierarchyData, tenant.id]);

  let content = (
    <CircularProgress
      ariaLabel={t('global.loadingIndicator.loading').toString()}
      data-testid="loading-data-sharing"
    />
  );

  if (hierarchyError) {
    content = <Typography data-testid="data-sharing-error">{t('global.fetchError')}</Typography>;
  } else if (!hierarchyLoading) {
    if (hierarchyTenants.length === 0) {
      content = <Typography>{t('dataSharing.noLinkableTenants')}</Typography>;
    } else {
      content = (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell colSpan={4} className={classes.groupHeader}>
                {t('dataSharing.tenantDetails')}
              </TableCell>
              <TableCell
                colSpan={2}
                className={`${classes.groupHeader} ${classes.sharingGroupHeader}`}
              >
                {t('dataSharing.sharing')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.validationHeader}>
                {t('dataSharing.tenantName')}
              </TableCell>
              <TableCell className={classes.validationHeader}>
                {t('dataSharing.tenantId')}
              </TableCell>
              <TableCell className={classes.validationHeader}>{t('dataSharing.type')}</TableCell>
              <TableCell className={classes.validationHeader}>
                {t('dataSharing.dataExport')}
              </TableCell>
              <TableCell className={`${classes.sharingHeader} ${classes.sharingFirstColumn}`}>
                {t('dataSharing.dataSharing')}
              </TableCell>
              <TableCell className={classes.sharingHeader}>{t('dataSharing.reporting')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hierarchyTenants.map((tenant: HierarchyTenant) => (
              <TableRow key={tenant.id}>
                <TableCell className={classes.tableCell}>
                  <Link
                    href={`/tenants/${tenant.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.tenantLink}
                  >
                    {tenant.name || t('tenantInformation.unset')}
                  </Link>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <div className={classes.tenantIdCell}>
                    <RegionMismatchWarning
                      tenantId={tenant.id}
                      tenantRegion={getComparedRegion(tenant, resourceRegions)?.region}
                      regionSource={getComparedRegion(tenant, resourceRegions)?.source}
                      requestingRegion={requestingRegion}
                    />
                    <Link
                      href={`/tenants/${tenant.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.tenantLink}
                    >
                      {tenant.id}
                    </Link>
                  </div>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {tenant.tenantType || t('tenantInformation.unset')}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <DataExportStatus
                    tenantType={tenant.tenantType}
                    learnDataExport={learnDataExport[tenant.id]}
                    jobsLinkTenantId={tenant.id}
                  />
                </TableCell>
                <TableCell className={`${classes.sharingCell} ${classes.sharingFirstColumn}`}>
                  <BooleanStatusIcon
                    size="medium"
                    value={!!tenant.dataSharingInd}
                    label={tenant.dataSharingInd ? t('global.yes') : t('global.no')}
                  />
                </TableCell>
                <TableCell className={classes.sharingCell}>
                  <BooleanStatusIcon
                    size="medium"
                    value={!!tenant.usedInReportingInd}
                    label={tenant.usedInReportingInd ? t('global.yes') : t('global.no')}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
  }

  return (
    <div className={classes.section}>
      <Typography className={classes.heading} component="h2" variant="h4">
        {t('dataSharing.pageTitle')}
      </Typography>
      {content}
    </div>
  );
};
